import useModalClass from 'hooks/useModalClass';
import { FC, Fragment, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faTimes } from '@fortawesome/free-solid-svg-icons';
import { showError, showSuccess } from 'utils/utils';
import { validate } from 'utils/validation';
import { createPortal } from 'react-dom';
import { OrderRow } from 'ts/shared/interfaces/MOResponse';
import { getStatus } from 'constants/mo';
import { sendReportPdfByEmail } from 'services/myOrders';
import ScreenshotSupport from '../ScreenshotSupport';
import MaximizeModal from '../MaximizeModal';
import useDraggableModal from 'hooks/useDraggableModal';
import { MainContext } from 'contexts/MainContext';

interface MoReportModalProps {
  onClose: () => void;
  isOpen: boolean;
  url: string;
  orderRow: OrderRow;
}

const MoReportModal: FC<MoReportModalProps> = ({ url, orderRow, ...props }) => {
  const { t } = useTranslation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const classList = useModalClass(props.isOpen);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);

  const [toMe, setToMe] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { user } = useContext(MainContext);
  const [email, setEmail] = useState(user?.email || '');

  const onClickSumit = async () => {
    const isValid = validate({ email: { required: !toMe, email: true } }, { email });
    setIsSubmitted(true);

    if (isValid) {

      const res = await sendReportPdfByEmail({
        email: email || user?.email || '',
        url,
      });

      if (res && !res.hasErrors) {
        props.onClose();
        showSuccess(t('mo.email_success'));
      } else {
        showError(t('something_went_wrong'));
      }

    }
  };

  const onClickPrint = async () => {
    window.open(url, '_blank');
  };

  return (
    <Fragment>
      {props.isOpen && createPortal(
        <div
          className="modal-overlay modal-overlay-top modal-maximizable" style={{ direction: 'ltr' }}
          ref={modalOverlayRef}
          onClick={(e) => e.target === e.currentTarget && props.onClose()}
        >
          <div ref={modalRef} className={classList} style={modalStyle}>
            <div
              className={'modal-title  draggable-modal-handle' + (isDragging ? ' dragging' : '')}
              {...dragHandleProps}
            >
              <div>
                <span className="title mr-10">
                  {t('mo.order_report')} {orderRow.segmentId}{' '}
                  {!!orderRow.status && (
                    <span className={`mo-status ${getStatus(orderRow.status)}`}>
                      {orderRow.status}
                    </span>
                  )}
                </span>
              </div>
              <div data-no-drag="true">
                <ScreenshotSupport />
                <MaximizeModal modalOverlayRef={modalOverlayRef} />
                <FontAwesomeIcon icon={faTimes} onClick={() => props.onClose()} />
              </div>
            </div>
            <div className="modal-content">
              <FormGroup
                className="mb-20"
                label={''}
                type="email"
                movablePlaceholder={t('passenger.email')}
                movablePlaceholderIcon={faEnvelope}
                value={email}
                onChange={setEmail}
                validation={{ required: !toMe, email: true }}
                isSubmitted={isSubmitted}
              />

              <Checkbox
                checked={toMe}
                onChange={setToMe}
                label={t('mo.send_to_me', { email: user?.email })}
              />
            </div>
            <div className="modal-footer">
              <div className='mr-a ml-0'>

                <Button
                  className="btn btn-primary"
                  title={t('mo.send')}
                  onClick={() => onClickSumit()}
                />
                <Button
                  className="btn btn-primary ml-5 mr-5"
                  title={t('mo.download')}
                  onClick={() => onClickPrint()}
                />
              </div>

              <Button
                className="btn btn-default"
                title={t('close')}
                onClick={() => props.onClose()}
              />
            </div>
          </div>
        </div>
        , document.body)}
    </Fragment>
  );
};

export default MoReportModal;
